import { useNavigation, useRoute } from '../../navigation/native';
import React, { useEffect, useState } from 'react';
import { Keyboard, Platform } from '../../web/react-native';
import { useDispatch } from 'react-redux';
import styled from '../../components/styled';
import {
  GetShiftLogsActionAsync,
  GetTeamShiftLogsActionAsync,
} from '../../../Action/ShiftLogAction';
import { EditingRowScreenProps } from '../../app/Navigation';
import { Route } from '../../app/Route';

import { FabButton, LoadingOverlay, NavBar, NavIconButton } from '../../components';
import { displayConfirmationAlert } from '../../helpers/Functions';
import { Event, trackEvent } from '../../services/Analytics';
import {
  selectCurrentShiftLog,
  selectCurrentSiteCode,
  selectEditingMode,
  selectHasEditSomeSnippet,
  selectIsLogDetailLoading,
  selectUser,
} from '../../store';
import { AppDispatch } from '../../store/store';
import SubmitButton from './SubmitButton';

interface LayoutProps {
  title: string;
  action?: React.ReactElement;
  header?: React.ReactElement;
  content: React.ReactElement;
  onPostComment?: () => void;
  onBackPressed?: () => void;
}

const Layout = ({ title, action, header, content, onPostComment, onBackPressed }: LayoutProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigation = useNavigation();
  const { params } = useRoute() as { params: Partial<EditingRowScreenProps> };

  const user = selectUser.useHook();
  const readOnly = selectEditingMode.useHook() === 'readOnly';
  const siteCode = selectCurrentSiteCode.useHook();
  const shiftLog = selectCurrentShiftLog.useHook();
  const isLoading = selectIsLogDetailLoading.useHook();
  const hasEditSomeSnippet = selectHasEditSomeSnippet.useHook();

  const [keyboardVisible, setKeyboardVisible] = useState(false);

  const canSubmit = user.name === shiftLog?.createUser;

  // REFACT: replace with a custom hook
  useEffect(() => {
    const showSubscription = Keyboard.addListener('keyboardDidShow', () => {
      setKeyboardVisible(true);
    });
    const hideSubscription = Keyboard.addListener(
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
      () => {
        setKeyboardVisible(false);
      },
    );

    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);

  useEffect(() => {
    return navigation.addListener('beforeRemove', (e) => {
      e.preventDefault();
      if (hasEditSomeSnippet) {
        displayConfirmationAlert({
          title: 'Confirm Cancel',
          description: 'Are you sure you want leave? Any changes to the shift log will be lost.',
          onConfirm: () => {
            trackEvent(Event.CancelShiftLogChanges);
            onBackPressed?.();
            navigation.dispatch(e.data.action);
          },
        });
      } else {
        navigation.dispatch(e.data.action);
      }
    });
  }, [dispatch, hasEditSomeSnippet, navigation, onBackPressed]);

  const handleSuccessfullySubmitted = async () => {
    // REFACT: this
    if (params.isTeamLog) {
      await dispatch(
        GetTeamShiftLogsActionAsync({
          siteCode: siteCode,
          assetName: shiftLog.assetName,
        }),
      );
    } else {
      await dispatch(
        GetShiftLogsActionAsync({
          siteCode: siteCode,
          assetName: shiftLog.assetName,
        }),
      );
    }
    navigation.navigate(Route.reportScreen);
  };

  const handleBackPressed = () => {
    navigation.goBack();
  };

  return (
    <>
      <NavBar
        title={title}
        leftButton={<NavBackButton icon={'chevron-left'} onPress={handleBackPressed} />}
        rightButton={
          readOnly
            ? undefined
            : action
            ? action
            : canSubmit && <SubmitButton onSuccess={handleSuccessfullySubmitted} />
        }
      />
      <LoadingOverlay visible={isLoading} />
      <SafeAreaView>
        <HeaderContainer>{header}</HeaderContainer>
        <Content>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            enabled
            keyboardVerticalOffset={100}>
            {/* REFACT: maybe content should have `ScrollView` so it could change to a `FlatList` when needed */}
            <ScrollView>
              {content}
              <Space />
            </ScrollView>
          </KeyboardAvoidingView>
        </Content>
      </SafeAreaView>

      {!readOnly && onPostComment && (
        <FabButton label="" onPress={onPostComment} icon="comment" visible={!keyboardVisible} />
      )}
    </>
  );
};

export default Layout;

const HeaderContainer = styled.View`
  padding-vertical: 1px;
  background: rgba(0, 0, 0, 0.05);
`;

const Content = styled.View`
  flex:  0 1 auto;
`;

const SafeAreaView = styled.SafeAreaView`
  flex: 1;
  background-color: #f3f7fa;
`;

const KeyboardAvoidingView = styled.KeyboardAvoidingView`
  flex: 1;
`;

const ScrollView = styled.ScrollView`
  padding: 0px 20px;
  max-height: 100vh;
  max-height: 100dvh ;
`;

const Space = styled.View`
  height: 50px;
`;

const NavBackButton = styled(NavIconButton)`
  flex: 1;
  align-items: flex-start;
`;
